<template>
  <WdsModal :show="show" lg @cancel="$emit('close')">
    <template #header>
      <h4>{{ $t('Timesheet for bill #:billno', { billno: paymentRequest?.number }) }}</h4>
    </template>

    <WdsStack v-if="loading" justify-center align-center class="tw-py-10">
      <WdsSpinner :size="40" />
    </WdsStack>

    <template v-else-if="paymentRequest">
      <div class="tw-text-base tw-flex tw-flex-row tw-gap-1 tw-pb-5">
        <strong>{{ $t('Worker') }}:</strong>
        <span>{{ paymentRequest.worker?.name }}</span>
      </div>

      <!-- Show structured timesheet data if available -->
      <div v-if="hasStructuredTimesheet">
        <!-- Day registrations -->
        <div v-if="hasDayRegistrations">
          <WdsCollapse
            v-model:open="showDailyRegistrations"
            :content-id="`daily-registrations-${props.paymentRequestId}`"
          >
            <template #title>
              <p class="tw-font-semibold tw-flex tw-items-center">
                {{ $t('Daily time registrations') }}
                <WdsIcon
                  class="tw-ml-2"
                  :name="showDailyRegistrations ? 'chevron-up' : 'chevron-down'"
                  :size="16"
                />
              </p>
            </template>
            <template #content>
              <table class="table">
                <thead>
                  <tr>
                    <th>{{ $t('Date') }}</th>
                    <th>{{ isDaily ? $t('Days') : $t('Hours') }}</th>
                    <th>{{ $t('Description') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="registration in dayRegistrations" :key="registration.id">
                    <td>{{ registration.date }}</td>
                    <td>{{ registration.duration }}</td>
                    <td>{{ registration.comments }}</td>
                  </tr>
                </tbody>
              </table>
            </template>
          </WdsCollapse>
        </div>

        <!-- Line registrations -->
        <div v-if="hasLineRegistrations" class="tw-mt-5">
          <WdsCollapse
            v-model:open="showTimesheetLines"
            :content-id="`timesheet-lines-${props.paymentRequestId}`"
          >
            <template #title>
              <p class="tw-font-semibold tw-flex tw-items-center">
                {{ $t('Timesheet lines summary') }}
                <WdsIcon
                  class="tw-ml-2"
                  :name="showTimesheetLines ? 'chevron-up' : 'chevron-down'"
                  :size="16"
                />
              </p>
            </template>
            <template #content>
              <table class="table">
                <thead>
                  <tr>
                    <th class="tw-w-1/4">{{ $t('Timesheet line') }}</th>
                    <th class="tw-w-1/4 text-end">{{ isDaily ? $t('Days') : $t('Hours') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(line, index) in formattedLineRegistrations" :key="index">
                    <td>{{ line.fieldValues }}</td>
                    <td class="text-end">
                      {{ line.duration }}
                      {{ line.isBillable ? '' : `(${$t('non-payable')})` }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </template>
          </WdsCollapse>
        </div>
      </div>

      <!-- Fallback to legacy timesheet HTML if no structured data -->
      <component
        :is="legacyTimesheetComponent"
        v-else-if="hasLegacyTimesheet && parsedLegacyTimesheet"
      />

      <!-- Fallback to plain text timesheet (comment-based) -->
      <div v-else-if="hasPlainTextTimesheet" class="tw-whitespace-pre-wrap">
        {{ paymentRequest.legacyTimesheet?.html }}
      </div>
    </template>

    <template #footer>
      <WdsButton default @click="$emit('close')">{{ $t('Close') }}</WdsButton>
    </template>
  </WdsModal>
</template>

<script lang="ts" setup>
import { computed, h, ref } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import {
  WdsModal,
  WdsButton,
  WdsStack,
  WdsSpinner,
  WdsCollapse,
  WdsIcon,
} from '@worksome/wds-components'
import { graphql } from '@/gql'
import { useTranslation } from '@/composables/translation'
import TimesheetDisplay from '@/components/messages/TimesheetDisplay.vue'
import { PER_PAGE_MAXIMUM } from '@/utils/constants'

const props = defineProps<{
  show: boolean
  paymentRequestId: string
}>()

defineEmits<{
  close: []
}>()

const { $t } = useTranslation()

const { result, loading } = useQuery(
  graphql(`
    query PaymentRequestTimesheet($id: ID!, $registrationsCount: Int!) {
      paymentRequest(id: $id) {
        id
        number
        rateType
        comments
        worker {
          name
        }
        timesheet {
          id
          registrations(first: $registrationsCount) {
            data {
              id
              type
              date
              duration
              comments
              isBillable
              customFieldValues {
                id
                displayValue
                customField {
                  title
                }
              }
            }
          }
        }
        legacyTimesheet {
          html
        }
      }
    }
  `),
  () => ({
    id: props.paymentRequestId,
    registrationsCount: PER_PAGE_MAXIMUM,
  }),
  () => ({
    enabled: props.show,
    clientId: 'publicApiCustomFields',
  }),
)

const paymentRequest = computed(() => result.value?.paymentRequest)

// Collapse toggles
const showDailyRegistrations = ref(true)
const showTimesheetLines = ref(true)

// Rate type helper
const isDaily = computed(() => paymentRequest.value?.rateType?.toLowerCase() === 'daily')

// Filter registrations by type
const allRegistrations = computed(() => paymentRequest.value?.timesheet?.registrations?.data || [])

const dayRegistrations = computed(() =>
  allRegistrations.value.filter((reg) => reg.type?.toUpperCase() === 'DAY'),
)

const lineRegistrations = computed(() =>
  allRegistrations.value.filter((reg) => reg.type?.toUpperCase() === 'LINE'),
)

const hasDayRegistrations = computed(() => dayRegistrations.value.length > 0)
const hasLineRegistrations = computed(() => lineRegistrations.value.length > 0)

// Check if there's any structured timesheet data (prioritize this over legacy)
const hasStructuredTimesheet = computed(
  () => hasDayRegistrations.value || hasLineRegistrations.value,
)

// Check if legacy timesheet HTML exists
const hasLegacyTimesheet = computed(() => !!paymentRequest.value?.legacyTimesheet?.html)

// Parse legacy timesheet HTML to extract component props
const parsedLegacyTimesheet = computed(() => {
  const html = paymentRequest.value?.legacyTimesheet?.html
  if (!html) {
    return null
  }

  // If it doesn't look like a component (no rate-type attribute), it's plain text - don't parse
  if (!html.includes('rate-type')) {
    return null
  }

  try {
    const rateTypeMatch = html.match(/rate-type\s*=\s*["']([^"']+)["']/)
    const rateType = rateTypeMatch ? rateTypeMatch[1] : undefined

    const rowsMatch = html.match(/:timesheet-rows\s*=\s*["']([\s\S]*?)["']/)
    let timesheetRows: unknown[] = []
    if (rowsMatch) {
      const decoded = rowsMatch[1]
        .replace(/&quot;/g, '"')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
      timesheetRows = JSON.parse(decoded)
    }

    const linesMatch = html.match(/:timesheet-lines\s*=\s*["']([\s\S]*?)["']/)
    let timesheetLines: unknown[] = []
    if (linesMatch) {
      const decoded = linesMatch[1]
        .replace(/&quot;/g, '"')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
      timesheetLines = JSON.parse(decoded)
    }

    return {
      rateType,
      timesheetRows: Array.isArray(timesheetRows) ? timesheetRows : [],
      timesheetLines: Array.isArray(timesheetLines) ? timesheetLines : [],
    }
  } catch {
    return null
  }
})

// Create component using h() function
const legacyTimesheetComponent = computed(() => {
  if (!parsedLegacyTimesheet.value) {
    return null
  }

  return h(TimesheetDisplay, {
    rateType: parsedLegacyTimesheet.value.rateType,
    timesheetRows: parsedLegacyTimesheet.value.timesheetRows,
    timesheetLines: parsedLegacyTimesheet.value.timesheetLines,
  })
})

// Fallback to plain text timesheet (comment-based, not structured HTML)
const hasPlainTextTimesheet = computed(() => {
  const html = paymentRequest.value?.legacyTimesheet?.html
  if (!html) return false
  // If it doesn't look like a component (no rate-type attribute), it's plain text
  return !html.includes('rate-type')
})

// Group line registrations by custom field values
const formattedLineRegistrations = computed(() => {
  const grouped = new Map()

  lineRegistrations.value.forEach((reg) => {
    const fieldValues =
      reg.customFieldValues
        ?.map((cfv) => `${cfv.customField?.title}: ${cfv.displayValue}`)
        .join(', ') || 'Default'

    if (!grouped.has(fieldValues)) {
      grouped.set(fieldValues, {
        fieldValues,
        duration: 0,
        isBillable: reg.isBillable,
      })
    }

    const line = grouped.get(fieldValues)
    line.duration += reg.duration
  })

  return Array.from(grouped.values())
})
</script>
